import { AlertTriangle, Database, ShieldCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useZalohyStav, type StavZaloh } from "@/api/hooks/useZalohy";
import { ObnovaZakazky } from "@/components/sprava/ObnovaZakazky";

/**
 * Stav zálohovania databázy.
 *
 * Prečo to appka vôbec ukazuje: **ticho zlyhaná záloha je horšia než
 * žiadna** — myslíš si, že ťa kryje. Bez tejto obrazovky by sa
 * o zastavených zálohách nikto nedozvedel až do dňa, keď ich treba.
 *
 * Stav je **len čítanie**. Tlačidlo „zálohuj teraz" by znamenalo, že
 * backend vie spúšťať procesy v cudzom kontajneri (docker socket = root
 * na hostiteľovi) — to sa kvôli pohodliu nerobí. Obnova zákazky (KOLO
 * 198) ide tiež bez neho: appka zapíše požiadavku do DB a kontajner si
 * ju vyzdvihne sám.
 */

const NADPIS: Record<StavZaloh, string> = {
  ok: "Zálohovanie beží",
  zastarane: "Zálohy sú zastarané",
  chyba: "Zálohovanie hlási chybu",
  nebezi: "Zálohovanie nebeží",
};

/** Čo s tým — nie iba „je zle". Kto to číta, bude v strese. */
const RADA: Record<StavZaloh, string> = {
  ok: "",
  zastarane:
    "Posledná záloha je staršia než dva dni. Zálohovací kontajner " +
    "pravdepodobne nebeží alebo je plný disk.",
  chyba:
    "Posledný pokus o zálohu zlyhal. Databáza beží ďalej, ale nová " +
    "záloha nevznikla.",
  nebezi:
    "Zálohovací kontajner zatiaľ nezapísal žiadny stav. Ak sa práve " +
    "nasadzovalo, chvíľu počkaj; inak nebeží.",
};

function vekText(hodin: number | null): string {
  if (hodin === null) return "nikdy";
  if (hodin < 1) return "pred menej než hodinou";
  if (hodin < 24) return `pred ${Math.round(hodin)} h`;
  const dni = Math.floor(hodin / 24);
  return dni === 1 ? "pred 1 dňom" : `pred ${dni} dňami`;
}

function velkostText(bajtov: number): string {
  if (bajtov <= 0) return "—";
  if (bajtov < 1024 * 1024) return `${(bajtov / 1024).toFixed(0)} kB`;
  return `${(bajtov / (1024 * 1024)).toFixed(1)} MB`;
}

function datumText(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("sk-SK", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Udaj({
  popis,
  hodnota,
}: {
  popis: string;
  hodnota: string;
}): JSX.Element {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{popis}</div>
      <div className="text-sm font-medium">{hodnota}</div>
    </div>
  );
}

export function ZalohySection(): JSX.Element {
  return (
    <div className="space-y-4">
      <StavZaloh />
      <ObnovaZakazky />
    </div>
  );
}

function StavZaloh(): JSX.Element {
  const query = useZalohyStav();
  const stav = query.data?.data;

  if (query.isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!stav) {
    return (
      <section className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Stav záloh sa nepodarilo načítať.
      </section>
    );
  }

  const vporiadku = stav.stav === "ok";

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Database className="h-5 w-5 text-primary" />
          Zálohy databázy
        </h2>
        <p className="text-xs text-muted-foreground">
          Zálohuje sa každú noc automaticky. Držia sa zálohy za posledných 30
          dní.
        </p>
      </div>

      <div
        className={
          vporiadku
            ? "flex items-start gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/10 p-3"
            : "flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3"
        }
      >
        {vporiadku ? (
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        )}
        <div className="min-w-0 space-y-1">
          <p
            className={
              vporiadku
                ? "text-sm font-medium"
                : "text-sm font-medium text-destructive"
            }
          >
            {NADPIS[stav.stav]}
          </p>
          {vporiadku ? (
            <p className="text-xs text-muted-foreground">
              Posledná záloha {vekText(stav.vek_hodin)}.
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {RADA[stav.stav]}
              </p>
              {stav.chyba ? (
                <p className="break-words font-mono text-[11px] text-muted-foreground">
                  {stav.chyba}
                </p>
              ) : null}
              {/* Konkrétny príkaz, nie „pozri logy" — kto to číta, rieši
                  práve teraz problém a nebude hľadať v dokumentácii */}
              <p className="text-xs text-muted-foreground">
                Na serveri:{" "}
                <code className="font-mono">
                  cd /opt/configurator &amp;&amp; docker compose logs --tail 50
                  backup
                </code>
              </p>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Udaj popis="Posledná záloha" hodnota={datumText(stav.posledna_zaloha)} />
        <Udaj popis="Veľkosť" hodnota={velkostText(stav.velkost_b)} />
        <Udaj popis="Počet záloh" hodnota={String(stav.pocet)} />
        <Udaj
          popis="Overená obnova"
          hodnota={
            stav.posledne_overenie
              ? `${stav.overenie_ok ? "OK" : "zlyhala"} · ${datumText(
                  stav.posledne_overenie,
                )}`
              : "zatiaľ nie"
          }
        />
      </div>

      {/* Príkaz `gunzip … | psql … tandem_budget` tu do KOLA 198 bol —
          a user ho skopíroval, keď chcel vrátiť jednu zákazku. Do bežiacej
          DB sa celý dump pustiť nesmie (padá na existujúcich riadkoch,
          vráti sekvencie dozadu), preto tu žiadny príkaz nie je */}
      <p className="text-xs text-muted-foreground">
        Raz týždenne sa záloha skúšobne obnoví do dočasnej databázy —
        neoverená záloha nie je záloha. Súbory sú na serveri v{" "}
        <code className="font-mono">{stav.adresar}</code>. Jednu zákazku
        vrátiš nižšie; celú databázu len pri jej strate podľa návodu
        v <code className="font-mono">DEPLOYMENT_CLOUD.md</code>.
      </p>
    </section>
  );
}
